/*
  Quaza Dex. Progressive enhancement over server-rendered links: every Pokémon, set, band and card
  is a real link without this file. This adds:
    appearance   light/dark, saved under Quaza Track's key so one choice covers both
    the bar      sticky under the store header; on the home it arrives once the big tabs scroll away
    lenses       Pokémon / Sets / Price, from the big tabs, the bar, or ?lens=
    the panel    on Pokémon and set pages (and later pages of the home) the bar's tabs drop the browser
                 down; its price bands become the home's live ones (sections/dex-prices)
    filters      the header's "filtered to" line follows the theme's in-place filter reloads
    search       Pokémon (English, Japanese, number, typo, type or region word, or a card name around it:
                 "charizard ex"), sets (name, code, series), cards (Shopify predictive search, rendered
                 by sections/dex-suggest so prices are in the visitor's currency). The Pokémon you typed
                 comes first, then its evolution line in order (Pichu, Raichu after Pikachu; all eight
                 after Eevee). A Pokémon we have no cards for answers as a silhouette with a way to ask.
    rails        arrows on desktop (the results rail too)
*/
(function () {
  'use strict';

  var TYPES = ['normal', 'fire', 'water', 'electric', 'grass', 'ice', 'fighting', 'poison', 'ground',
    'flying', 'psychic', 'bug', 'rock', 'ghost', 'dragon', 'dark', 'steel', 'fairy'];
  var REGIONS = ['kanto', 'johto', 'hoenn', 'sinnoh', 'unova', 'kalos', 'alola', 'galar', 'paldea'];
  var html = document.documentElement;
  var mq = function (q) { return window.matchMedia ? window.matchMedia(q) : { matches: false }; };
  var canHover = mq('(hover: hover)').matches;
  var reduceMotion = mq('(prefers-reduced-motion: reduce)').matches;
  var KEY = 'qpt-theme';

  function cap(s) { return s.charAt(0).toUpperCase() + s.slice(1); }
  function pad(n) { return n > 999 ? String(n) : ('00' + n).slice(-3); }
  function esc(s) { return String(s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }
  // a picture fetched now: a far rail tile's held source (dex-sprite `later`) goes in, and lazy stops waiting
  function wake(img) {
    if (img.dataset.dxSrcset) { img.srcset = img.dataset.dxSrcset; img.removeAttribute('data-dx-srcset'); }
    if (img.dataset.dxSrc) { img.src = img.dataset.dxSrc; img.removeAttribute('data-dx-src'); }
    img.loading = 'eager';
  }
  // held rail tiles fill in as they come on screen. The viewport is the root, so a tile scrolled out of
  // sight inside its rail is clipped away and waits; one a desktop shows from the start fills at once.
  var held = null;
  function holdUntilSeen(scope) {
    var imgs = scope.querySelectorAll('img[data-dx-src]');
    if (!imgs.length) return;
    if (!window.IntersectionObserver) { Array.prototype.forEach.call(imgs, wake); return; }
    held = held || new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { if (e.isIntersecting) { held.unobserve(e.target); wake(e.target); } });
    }, { rootMargin: '0px 0px 200px 0px' });
    Array.prototype.forEach.call(imgs, function (img) { held.observe(img); });
  }
  // once the first row of photos is in (or the page has loaded, if that comes first), the card photos
  // 2500px below the screen come too, the distance Chrome's own lazy loading looks ahead on a slow line,
  // so a scroll through the grid finds them drawn (dex-card holds every photo from the fifth card)
  var ahead = null;
  function warmGrid() {
    var imgs = document.querySelectorAll('.dx-card__img img[data-dx-src]');
    if (!imgs.length || !window.IntersectionObserver) return;
    ahead = ahead || new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { if (e.isIntersecting) { ahead.unobserve(e.target); if (held) held.unobserve(e.target); wake(e.target); } });
    }, { rootMargin: '0px 0px 2500px 0px' });
    Array.prototype.forEach.call(imgs, function (img) { ahead.observe(img); });
  }
  function afterFirstPhotos(cb) {
    var done = false;
    var go = function () { if (!done) { done = true; cb(); } };
    var first = Array.prototype.filter.call(document.querySelectorAll('.dx-card__img img[fetchpriority="high"]'), function (img) { return !img.complete; });
    var left = first.length;
    if (document.readyState === 'complete' || !left) return go();
    window.addEventListener('load', go);
    first.forEach(function (img) {
      var one = function () { if (--left === 0) go(); };
      img.addEventListener('load', one, { once: true });
      img.addEventListener('error', one, { once: true });
    });
  }
  // lowercase, no accents (Flabébé), no punctuation (Mr. Mime), hiragana folded to katakana (ぴかちゅう → ピカチュウ)
  function norm(s) {
    s = String(s || '').normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase();
    s = s.replace(/[ぁ-ゖ]/g, function (c) { return String.fromCharCode(c.charCodeAt(0) + 0x60); });
    return s.replace(/[^a-z0-9゠-ヿ一-鿿]+/g, '');
  }
  // Damerau-Levenshtein with an early exit past the cap
  function dist(a, b, max) {
    if (Math.abs(a.length - b.length) > max) return max + 1;
    var d = [], i, j;
    for (i = 0; i <= a.length; i++) d[i] = [i];
    for (j = 1; j <= b.length; j++) d[0][j] = j;
    for (i = 1; i <= a.length; i++) {
      var best = max + 1;
      for (j = 1; j <= b.length; j++) {
        var c = a[i - 1] === b[j - 1] ? 0 : 1;
        d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + c);
        if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
        if (d[i][j] < best) best = d[i][j];
      }
      if (best > max) return max + 1;
    }
    return d[a.length][b.length];
  }
  function textScore(nn, qn) {
    if (!nn || !qn) return 0;
    if (nn === qn) return 10;
    if (nn.indexOf(qn) === 0) return 8;
    if (nn.indexOf(qn) !== -1) return 6;
    if (qn.length >= 4) {
      var max = qn.length >= 7 ? 2 : 1;
      if (dist(qn, nn, max) <= max) return 5;
      if (nn.length > qn.length && dist(qn, nn.slice(0, qn.length), max) <= max) return 3;
    }
    return 0;
  }
  function monScore(it, q, qn) {
    if (/^#?\d+$/.test(q.trim())) {
      var n = parseInt(q.replace('#', ''), 10);
      if (it.d === n) return 9;
      return String(it.d).indexOf(String(n)) === 0 ? 4 : 0;
    }
    var s = textScore(it.nn, qn);
    if (it.jn) s = it.jn === qn ? 10 : it.jn.indexOf(qn) !== -1 ? Math.max(s, 7) : s;
    // the Pokémon inside a card name: "charizard ex", "pikachu vmax", "galarian meowth", "team rocket's mewtwo"
    // (four letters or more, so Mew is not found inside "mewtwo")
    if (s < 7 && it.nn.length >= 4 && qn.indexOf(it.nn) !== -1) s = 7;
    return s;
  }

  /* ---------- appearance */
  function setMode(v, animate) {
    var apply = function () {
      html.setAttribute('data-dex', v);
      try { localStorage.setItem(KEY, v); } catch (e) { /* private browsing */ }
      labelModes();
    };
    // The swap fades. Chromium crossfades two snapshots of the page on the compositor (a view
    // transition, quaza-dex.css html.dx-vt); a colour transition on every element of the page
    // repainted it for 0.28s on the main thread, and a phone's tap took ~450ms to answer (measured
    // 2026-10-13, Moto profile; ~250ms of that is the restyle itself). WebKit keeps the colour fade:
    // its view transitions were slow here (snippets/dex-boot).
    if (animate && !reduceMotion && document.startViewTransition && navigator.userAgentData) {
      html.classList.add('dx-vt');
      var vt = document.startViewTransition(apply);
      vt.finished.then(function () { html.classList.remove('dx-vt'); }, function () { html.classList.remove('dx-vt'); });
      return;
    }
    if (animate && !reduceMotion) {
      html.classList.add('dx-swapping');
      clearTimeout(setMode.t);
      setMode.t = setTimeout(function () { html.classList.remove('dx-swapping'); }, 360);
    }
    apply();
  }
  function labelModes() {
    var dark = html.getAttribute('data-dex') === 'dark';
    document.querySelectorAll('[data-dx-mode]').forEach(function (b) {
      b.setAttribute('aria-label', dark ? 'Switch to light mode' : 'Switch to dark mode');
    });
  }

  /* ---------- the bar docks under the store header, wherever that header rests.
     Past 250px Impulse makes #SiteHeader position:fixed, parks it just above the screen, then 100ms
     later slides it in over 0.4s (theme.css .site-header--stuck / --opening). While parked the bar docks
     at the top. Once the slide starts, --dx-top jumps to the header's resting edge and
     html[data-dx-head="slide"] gives everything docked under it the header's own transition
     (quaza-dex.css), so they move together on the same clock instead of a frame behind. */
  var dxTop = 0, dxHead = '', dxRest = 0;
  function setTop(h) { if (h !== dxTop) { dxTop = h; html.style.setProperty('--dx-top', h + 'px'); } }
  function measureTop() {
    var h = 0, state = '', header = document.querySelector('.site-header');
    if (header && header.classList.contains('site-header--stuck')) {
      state = header.classList.contains('site-header--opening') ? 'slide' : 'parked';
    }
    if (state !== dxHead) {
      // the slide starts now: give the docked rows its end point before anything reads a style, so
      // their transition starts on the same style pass as the header's (a read would start its first)
      if (state === 'slide' && dxHead === 'parked' && dxRest) setTop(dxRest);
      dxHead = state;
      if (state) html.setAttribute('data-dx-head', state); else html.removeAttribute('data-dx-head');
    }
    if (header) {
      var cs = getComputedStyle(header);
      var par = getComputedStyle(header.parentElement || header).position;
      if (cs.position === 'fixed' || cs.position === 'sticky' || par === 'fixed' || par === 'sticky') {
        // where the header rests: its parking or slide transform left out
        var bottom = header.getBoundingClientRect().bottom, ty = 0;
        if (state && cs.transform && cs.transform !== 'none' && window.DOMMatrixReadOnly) ty = new DOMMatrixReadOnly(cs.transform).m42;
        dxRest = Math.max(0, Math.round(bottom - ty));
        h = state === 'parked' ? Math.max(0, Math.round(bottom)) : dxRest;
      }
    }
    setTop(h);
    return h;
  }

  /* ---------- one browser (inline on the home, or inside the panel) */
  function Browser(root, opts) {
    var self = this;
    var $ = function (s) { return root.querySelector(s); };
    var input = $('[data-dx-q]'), clear = $('[data-dx-clear]'), form = $('[data-dx-form]');
    var results = $('[data-dx-results]');
    // One short line for screen readers once the results settle: what each group is, or the "nothing"
    // note. The results area is not live itself, or it would be read out on every keystroke.
    var status = $('[data-dx-status]'), statusTimer = 0;
    function announce() {
      if (!status) return;
      clearTimeout(statusTimer);
      statusTimer = setTimeout(function () {
        var none = results.querySelector('.dx-results__empty');
        var heads = Array.prototype.filter.call(results.querySelectorAll('.dx-results__group'), function (g) { return !g.hidden; })
          .map(function (g) { return g.firstChild.textContent; });
        status.textContent = !searching ? '' : none ? none.textContent : heads.length ? 'Showing ' + heads.join(', ') : '';
      }, 600);
    }
    var lenses = Array.prototype.slice.call(root.querySelectorAll('[data-dx-lens]'));
    var lensTabs = Array.prototype.slice.call(root.querySelectorAll('[data-dx-lenstab]'));
    var art = root.dataset.art || 'home';
    var lens = 'pokemon', q = '', searching = false;
    self.root = root;
    self.input = input;

    var mons = Array.prototype.map.call(root.querySelectorAll('[data-dx-mons] > li'), function (el) {
      return { el: el, d: +el.dataset.d, n: el.dataset.n, nn: norm(el.dataset.n), j: el.dataset.j, jn: norm(el.dataset.j),
        t: (el.dataset.t || '').split(' '), g: +el.dataset.g, c: +el.dataset.c, i: +el.dataset.i };
    });
    var sets = Array.prototype.map.call(root.querySelectorAll('[data-dx-sets] > li[data-k]'), function (el) {   // not the Misc divider
      var a = el.querySelector('a');
      return { el: el, n: el.dataset.n, nn: norm(el.dataset.n), code: norm(el.dataset.code), cd: el.dataset.code || '', l: el.dataset.l, y: el.dataset.y,
        se: norm(el.dataset.se), c: +el.dataset.c, href: a ? a.getAttribute('href') : '#', logo: el.dataset.logo };
    });

    // every species (assets/quaza-dex-species.json): names, types, generation, and the evolution chain each
    // belongs to with the species it evolves from. Fetched once, when the search is first reached for.
    var species = null, speciesP = null, byDex = {}, lines = {};
    function loadSpecies() {
      if (!speciesP) {
        speciesP = fetch(root.dataset.species, { credentials: 'omit' })
          .then(function (r) { return r.ok ? r.json() : []; })
          .then(function (rows) {
            species = rows.map(function (r) {
              return { d: r[0], n: r[1], nn: norm(r[1]), j: r[2], jn: norm(r[2]), t: r[3].split(','), g: r[4], f: r[5] || 0, e: r[6] || 0 };
            });
            species.forEach(function (s) { byDex[s.d] = s; });
            // an evolution line reads first form, then each stage; branches by National Dex number
            // (Pichu, Pikachu, Raichu; Eevee, then Vaporeon to Sylveon)
            species.forEach(function (s) {
              var st = 0, cur = s;
              while (cur.e && byDex[cur.e] && st < 4) { st++; cur = byDex[cur.e]; }
              s.st = st;
              if (s.f) (lines[s.f] = lines[s.f] || []).push(s);
            });
            Object.keys(lines).forEach(function (k) { lines[k].sort(function (a, b) { return a.st - b.st || a.d - b.d; }); });
            return species;
          })
          .catch(function () { species = []; return species; });
      }
      return speciesP;
    }
    // reached for: fetch now, so the line is there by the second letter
    ['focus', 'pointerdown'].forEach(function (ev) { input.addEventListener(ev, loadSpecies, { passive: true }); });

    /* lenses */
    self.setLens = function (k, fromUser) {
      // checked against the list, not a selector: a quote in ?lens= made querySelector throw and stopped the script
      if (!lenses.some(function (el) { return el.dataset.dxLens === k; })) k = 'pokemon';
      lens = k;
      lenses.forEach(function (el) { el.hidden = searching || el.dataset.dxLens !== k; });
      lensTabs.forEach(function (b) {
        var on = b.dataset.dxLenstab === k;
        b.setAttribute('aria-selected', on ? 'true' : 'false');
        b.tabIndex = on ? 0 : -1;
      });
      if (opts.onLens) opts.onLens(k, fromUser);
    };
    self.lens = function () { return lens; };
    lensTabs.forEach(function (b, i) {
      b.addEventListener('click', function () { self.setLens(b.dataset.dxLenstab, true); });
      b.addEventListener('keydown', function (e) {
        var d = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
        if (!d) return;
        e.preventDefault();
        var nb = lensTabs[(i + d + lensTabs.length) % lensTabs.length];
        nb.focus();
        self.setLens(nb.dataset.dxLenstab, true);
      });
    });

    /* search */
    var ctl = null, cardTimer = 0, runTimer = 0, seq = 0;
    function spriteUrl(d, w) { return '/cdn/shop/files/dex-' + art + '-' + ('000' + d).slice(-4) + '.png?width=' + w; }
    // a Pokémon we have no cards for: its silhouette, and the whole tile asks us for it (as big a target as
    // the tiles around it, not just the two words)
    function uncaught(s) {
      var li = document.createElement('li');
      li.className = 'dx-mon dx-mon--uncaught';
      li.innerHTML = '<a class="dx-mon__a" target="_blank" rel="noopener"><span class="dx-mon__art"><img class="dx-sprite" alt="" width="480" height="480" loading="eager" decoding="async" src="' +
        spriteUrl(s.d, 180) + '" srcset="' + spriteUrl(s.d, 120) + ' 120w, ' + spriteUrl(s.d, 240) + ' 240w" sizes="84px"></span>' +
        '<span class="dx-mon__name"></span><span class="dx-mon__meta">Ask us</span></a>';
      li.querySelector('.dx-mon__name').textContent = s.n;
      var a = li.querySelector('a');
      a.href = root.dataset.ask;
      a.setAttribute('aria-label', 'No ' + s.n + ' cards right now. Ask us on Instagram');
      return li;
    }
    function group(title) {
      var g = document.createElement('div');
      g.className = 'dx-results__group';
      g.innerHTML = '<p class="dx-results__h"></p>';
      g.firstChild.textContent = title;
      return g;
    }
    function setRow(s) {
      var a = document.createElement('a');
      a.className = 'dx-row';
      a.href = s.href;
      a.setAttribute('data-dx-hit', '');
      a.innerHTML = '<span class="dx-row__img">' + (s.logo ? '<img alt="" loading="lazy" decoding="async" src="/cdn/shop/' + esc(s.logo) + '?width=160">' : '') + '</span>' +
        '<span class="dx-row__txt"><span class="dx-row__t"></span><span class="dx-row__m"></span></span>';
      a.querySelector('.dx-row__t').textContent = s.n;
      a.querySelector('.dx-row__m').textContent = (s.l === 'JP' ? 'Japanese' : 'English') + (s.y ? ' · ' + s.y : '') + (s.cd ? ' · ' + s.cd : '');   // the set code as printed (FFI, SV2a), not a card count: that goes stale
      return a;
    }

    function run() {
      var raw = q, qn = norm(raw), my = ++seq;
      runTimer = 0;
      // only spaces or punctuation is no search (the lenses stay). Letters norm() can't read (Korean,
      // say) still are one, and get the "nothing in the Dex" note rather than an empty area.
      searching = !!qn || /[^\s!-\/:-@\[-`{-~]/.test(raw);
      results.textContent = '';
      root.classList.toggle('is-searching', searching);
      if (!searching) { results.hidden = true; self.setLens(lens); announce(); if (opts.onQuery) opts.onQuery(''); return; }
      results.hidden = false;
      lenses.forEach(function (el) { el.hidden = true; });
      if (!qn) { empty(); if (opts.onQuery) opts.onQuery(raw); return; }

      var typeWord = TYPES.indexOf(qn) !== -1 ? qn : '';
      var region = REGIONS.indexOf(qn) + 1;

      var gMon = group('Pokémon');
      var ol = document.createElement('ol');
      ol.className = 'dx-rail__track';
      var railWrap = document.createElement('div');
      railWrap.className = 'dx-rail dx-rail--one';
      railWrap.appendChild(ol);
      gMon.appendChild(railWrap);
      results.appendChild(gMon);
      var ours = {}, railDex = {};   // railDex: every Pokémon in the row, which picks the cards below
      mons.forEach(function (m) { ours[m.d] = m; });
      // one of our Pokémon, as the home's tile; `hit` = it answers the query itself (Enter opens the first)
      var tile = function (m, hit) {
        var c = m.el.cloneNode(true);
        var a = c.querySelector('a');
        if (a && hit) a.setAttribute('data-dx-hit', '');
        c.querySelectorAll('img').forEach(function (img) { wake(img); img.removeAttribute('fetchpriority'); });
        return c;
      };
      var sep = function () {
        var li = document.createElement('li');
        li.className = 'dx-rail__sep';
        li.setAttribute('aria-hidden', 'true');
        return li;
      };
      var showRail = function () {
        gMon.hidden = !ol.children.length;
        if (canHover) { arrows(railWrap); if (railWrap.__upd) railWrap.__upd(); }
      };

      if (typeWord || region) {
        // a type or region word lists our Pokémon of that type or region, in National Dex order
        gMon.firstChild.textContent = typeWord ? cap(typeWord) + ' type' : cap(REGIONS[region - 1]) + ' Pokémon';
        mons.filter(function (m) { return typeWord ? m.t.indexOf(typeWord) !== -1 : m.g === region; })
          .sort(function (a, b) { return a.d - b.d; })
          .forEach(function (m) { ol.appendChild(tile(m, true)); });
        showRail();
      } else {
        fillMons();
        // the first search can beat the species file; the line arrives a moment later
        if (!species) loadSpecies().then(function () { if (my === seq) { fillMons(); empty(); } });
      }

      // The Pokémon rail. Whatever matches best leads (ours first on a tie), then the rest of its evolution
      // line in order, with silhouettes for the ones we have no cards for; then any other Pokémon the words
      // match. A near-spelling only counts when nothing is spelled that way: "pikachu" brings Pichu as
      // Pikachu's line, not as a typo, and "pickachu" still finds Pikachu. Scores: monScore / textScore.
      function fillMons() {
        ol.textContent = '';
        railDex = {};
        var numeric = /^#?\d+$/.test(raw.trim());
        var cand = [];
        mons.forEach(function (m) { var s = monScore(m, raw, qn); if (s > 0) cand.push({ d: m.d, s: s, m: m }); });
        if (species) species.forEach(function (sp) {
          if (ours[sp.d]) return;
          var s = monScore(sp, raw, qn);
          if (s > 0) cand.push({ d: sp.d, s: s, sp: sp });
        });
        // the best kind of match sets the bar: names that start with the word drop the ones that only hide it
        // ("char" is Charmander, not Pecharunt), and any real match drops near-spellings
        var best = cand.reduce(function (b, c) { return Math.max(b, c.s); }, 0);
        var bar = numeric ? 0 : best >= 8 ? 7 : best >= 6 ? 6 : 0;
        cand = cand.filter(function (c) { return c.s >= bar; });
        cand.sort(function (a, b) { return b.s - a.s || (a.m ? 0 : 1) - (b.m ? 0 : 1) || a.d - b.d; });

        var lead = cand[0], shown = {}, nOurs = 0, nAsk = 0, lastAsk = null;
        var put = function (d, hit) {
          if (shown[d]) return;
          shown[d] = railDex[d] = true;
          if (ours[d]) { ol.appendChild(tile(ours[d], hit)); nOurs++; }
          else if (byDex[d]) { ol.appendChild(uncaught(byDex[d])); nAsk++; lastAsk = byDex[d]; }
        };
        var line = lead && byDex[lead.d] && byDex[lead.d].f ? lines[byDex[lead.d].f] : [];
        if (lead) {
          put(lead.d, true);
          line.forEach(function (x) { put(x.d, cand.some(function (c) { return c.d === x.d; })); });
        }
        // other matches: ours, then up to four silhouettes, only for a full name or number, a word of four
        // letters or more, or when nothing of ours matches at all ("pik" no longer strands Pikipek)
        var rest = cand.filter(function (c) { return !shown[c.d]; });
        var anyOurs = cand.some(function (c) { return c.m; });
        var others = rest.filter(function (c) { return c.m; }).concat(rest.filter(function (c) {
          return !c.m && (c.s >= 9 || (qn.length >= 4 && c.s >= 7) || !anyOurs);
        }).slice(0, 4));
        if (others.length && ol.children.length > 1) ol.appendChild(sep());
        others.forEach(function (c) { put(c.d, true); });
        if (ol.lastChild && ol.lastChild.className === 'dx-rail__sep') ol.removeChild(ol.lastChild);

        // the heading says what the row is: one Pokémon and its line, or nothing of these in stock
        var inLine = function (c) { return line.some(function (x) { return x.d === c.d; }); };
        var title = 'Pokémon';
        if (!nOurs && nAsk) title = nAsk === 1 ? 'No ' + lastAsk.n + ' cards right now' : 'None of these in stock right now';
        else if (lead && line.length > 1 && cand.every(inLine)) title = (ours[lead.d] || byDex[lead.d]).n + ' and its evolution line';
        gMon.firstChild.textContent = title;
        showRail();
      }

      // sets answer type and region words too ("dark" is also Darkness Ablaze, "fire" Gouging Fire)
      var setHits = sets.map(function (s) {
        var sc = textScore(s.nn, qn);
        if (s.code && s.code === qn) sc = Math.max(sc, 9);
        if (qn.length >= 2 && s.se && s.se.indexOf(qn) === 0) sc = Math.max(sc, 4);
        if ((qn === 'japanese' || qn === 'jp') && s.l === 'JP') sc = 4;
        if (/^\d{4}$/.test(qn) && s.y === qn) sc = 4;
        s.s = sc;
        return s;
      }).filter(function (s) { return s.s > 0; }).sort(function (a, b) { return b.s - a.s; }).slice(0, 5);
      if (setHits.length) {
        var gSet = group('Sets');
        setHits.forEach(function (s) { gSet.appendChild(setRow(s)); });
        results.appendChild(gSet);
      }

      // cards: Shopify predictive search, rendered by sections/dex-suggest
      var gCard = group('Cards');
      gCard.hidden = true;
      results.appendChild(gCard);
      if (!typeWord && !region) {
        var box = document.createElement('div');
        box.innerHTML = '<div class="dx-skel"></div>';
        gCard.appendChild(box);
        gCard.hidden = false;
        clearTimeout(cardTimer);
        cardTimer = setTimeout(function () {
          if (ctl) ctl.abort();
          ctl = window.AbortController ? new AbortController() : null;
          var url = (window.Shopify && Shopify.routes && Shopify.routes.root || '/') + 'search/suggest?q=' + encodeURIComponent(raw) +
            '&section_id=dex-suggest&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=last';
          // the cards are picked by the Pokémon row, so its evolution line has to be there first
          Promise.all([
            fetch(url, { signal: ctl ? ctl.signal : undefined, credentials: 'same-origin' }).then(function (r) { return r.ok ? r.text() : ''; }),
            loadSpecies()
          ])
            .then(function (res) {
              if (my !== seq) return;
              var doc = new DOMParser().parseFromString(res[0], 'text/html');
              var list = doc.querySelector('[data-dx-suggest]');
              box.textContent = '';
              // a card of a Pokémon in the row above (Pikachu's line for "pikachu", or for the typo "pickachu"),
              // or one whose title and row hold every word typed (a trainer, a set or its code, a number, a
              // rarity, "1st edition"); not Shopify's look-alikes
              var words = raw.split(/\s+/).map(norm).filter(Boolean);
              var rows = list ? Array.prototype.filter.call(list.querySelectorAll('a.dx-row'), function (a) {
                if ((a.getAttribute('data-d') || '').split(' ').some(function (d) { return railDex[d]; })) return true;
                var hay = norm((a.getAttribute('data-t') || '') + ' ' + (a.querySelector('.dx-row__txt') ? a.querySelector('.dx-row__txt').textContent : ''));
                return words.every(function (w) { return hay.indexOf(w) !== -1; });
              }).slice(0, 6) : [];
              Array.prototype.forEach.call(rows, function (a) { a.setAttribute('data-dx-hit', ''); box.appendChild(document.importNode(a, true)); });
              if (rows.length) {
                var all = document.createElement('a');
                all.className = 'dx-results__all';
                all.href = (window.Shopify && Shopify.routes && Shopify.routes.root || '/') + 'search?type=product&q=' + encodeURIComponent(raw);
                all.textContent = 'All results for “' + raw.trim() + '”';
                box.appendChild(all);
              } else {
                gCard.hidden = true;
              }
              empty();
            })
            .catch(function (e) { if (e && e.name === 'AbortError') return; box.textContent = ''; gCard.hidden = true; empty(); });
        }, 140);
      }
      empty();

      function empty() {
        if (my !== seq) return;
        announce();
        var old = results.querySelector('.dx-results__empty');
        var any = Array.prototype.some.call(results.querySelectorAll('.dx-results__group'), function (g) { return !g.hidden; });
        if (any) { if (old) old.remove(); return; }
        if (old) return;
        var p = document.createElement('p');
        p.className = 'dx-results__empty';
        p.innerHTML = 'Nothing in the Dex for “<span></span>” yet. Try a Pokémon, a set name or a card number, or <a target="_blank" rel="noopener">ask us</a> for it.';
        p.querySelector('span').textContent = raw.trim();
        p.querySelector('a').href = root.dataset.ask;
        results.appendChild(p);
      }
      if (opts.onQuery) opts.onQuery(raw);
    }

    self.search = function (v, now) {
      q = v;
      if (input.value !== v) input.value = v;
      clear.hidden = !v;
      clearTimeout(runTimer);
      if (now) run(); else runTimer = setTimeout(run, 60);
    };
    input.addEventListener('input', function () { self.search(input.value); });
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && input.value) { e.preventDefault(); e.stopPropagation(); self.search('', true); }
    });
    clear.addEventListener('click', function () { self.search('', true); input.focus(); });
    form.addEventListener('submit', function (e) {
      // Enter straight after typing or pasting: this query's hits, not the last one's
      if (runTimer) { clearTimeout(runTimer); run(); }
      var first = results.querySelector('[data-dx-hit]');
      if (q && first) { e.preventDefault(); input.blur(); first.click(); }
      else if (!q) e.preventDefault();
    });
  }

  /* ---------- desktop rail arrows */
  var railUpds = [], railResize = false;
  function arrows(rail) {
    var track = rail.querySelector('.dx-rail__track');
    if (!track || rail.__dx) return;
    rail.__dx = true;
    var mk = function (dir) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'dx-rail__arrow dx-rail__arrow--' + dir;
      b.setAttribute('aria-label', dir === 'prev' ? 'Scroll back' : 'Scroll on');
      b.innerHTML = '<svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="' +
        (dir === 'prev' ? 'm15 5-7 7 7 7' : 'm9 5 7 7-7 7') + '"/></svg>';
      b.addEventListener('click', function () { track.scrollBy({ left: (dir === 'prev' ? -1 : 1) * track.clientWidth * 0.85, behavior: reduceMotion ? 'auto' : 'smooth' }); });
      rail.appendChild(b);
      return b;
    };
    var prev = mk('prev'), next = mk('next');
    var upd = function () {
      prev.disabled = track.scrollLeft < 4;
      next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
    };
    track.addEventListener('scroll', upd, { passive: true });
    upd.rail = rail;
    // search rebuilds its rail each keystroke: drop the ones gone from the page as new ones arrive
    railUpds = railUpds.filter(function (u) { return u.rail.isConnected; });
    railUpds.push(upd);
    if (!railResize) {
      // one resize listener for every rail
      railResize = true;
      window.addEventListener('resize', function () {
        railUpds = railUpds.filter(function (u) { return u.rail.isConnected; });
        railUpds.forEach(function (u) { u(); });
      });
    }
    upd();
    rail.__upd = upd;
  }

  /* ---------- the page */
  function init() {
    labelModes();
    holdUntilSeen(document);
    afterFirstPhotos(warmGrid);
    document.addEventListener('click', function (e) {
      var m = e.target.closest && e.target.closest('[data-dx-mode]');
      if (m) setMode(html.getAttribute('data-dex') === 'dark' ? 'light' : 'dark', true);
    });

    // The kind switch (snippets/dex-kind-switch, its state in dex-boot's window.dxk). One listener for the page: the
    // theme's filters replace the grid's HTML (theme.js), switch included, so a listener on the switch would go with
    // it. A tile or a step changes the pick in place; a step that is a link (the main Dex's conditions, its own
    // condition filter) is followed as it is, and a tile on such a filtered page leads back to the unfiltered one
    // (data-dxk-href). Either way the pick is kept for the next Dex page.
    if (window.dxk) {
      dxk.settle();
      document.addEventListener('click', function (e) {
        var b = e.target.closest && e.target.closest('.dxk [data-dxk-set], .dxk [data-dxk-step]');
        if (!b) return;
        var c = b.closest('.dxk'), K = dxk.kind(), S = dxk.step(), k, s;
        if (b.hasAttribute('data-dxk-step')) {
          k = b.getAttribute('data-dxk-kind');
          s = b.getAttribute('data-dxk-step');
          // the step that's on, tapped again: back to its kind's All
          if (s && s === S) s = '';
        } else {
          k = b.getAttribute('data-dxk-set');
          s = '';
          // the kind that's on, tapped again: the slid tile reads its kind's All, so it clears a step and stays;
          // a kind with one choice brings both kinds back
          if (k !== 'all' && k === K && !dxk.opens(c, k)) k = 'all';
        }
        dxk.keep(k, s);
        if (b.tagName === 'A') return;
        e.preventDefault();
        if (b.hasAttribute('data-dxk-href')) { location.assign(b.getAttribute('data-dxk-href')); return; }
        dxk.set(k, s);
        dxk.paint(true);
      });
      document.addEventListener('collection:reloaded', function () { dxk.settle(); });
    }

    var bar = document.querySelector('[data-dx-bar]');
    var isHome = bar && bar.hasAttribute('data-home');
    var tabs = bar ? Array.prototype.slice.call(bar.querySelectorAll('[data-dx-tab]')) : [];
    var params = new URLSearchParams(location.search);
    // ?dq= and ?lens= keep the search and the tab on reload; one write a moment after the last change,
    // carrying every key that changed (a tab tap clears the search and sets the lens together)
    var urlTimer = 0, urlNext = {};
    function writeUrl(k, v) {
      urlNext[k] = v;
      clearTimeout(urlTimer);
      urlTimer = setTimeout(function () {
        var p = new URLSearchParams(location.search);
        Object.keys(urlNext).forEach(function (key) { if (urlNext[key]) p.set(key, urlNext[key]); else p.delete(key); });
        urlNext = {};
        var s = p.toString();
        history.replaceState(history.state, '', location.pathname + (s ? '?' + s : '') + location.hash);
      }, 300);
    }

    // inline browser (home) or panel browser (Pokémon and set pages)
    var inlineEl = document.querySelector('.dx-browser--inline[data-dx-browser]');
    var panel = bar && bar.querySelector('[data-dx-panel]');
    var scrim = document.querySelector('[data-dx-scrim]');
    var browser = null;
    var lensTabsRow = document.querySelector('[data-dx-lenstabs]');

    if (inlineEl) {
      browser = new Browser(inlineEl, {
        onLens: function (k, fromUser) {
          tabs.forEach(function (b) { b.setAttribute('aria-pressed', b.dataset.dxTab === k ? 'true' : 'false'); });
          if (fromUser) writeUrl('lens', k === 'pokemon' ? '' : k);
        },
        onQuery: function (v) { writeUrl('dq', v.trim()); }
      });
      tabs.forEach(function (b) {
        b.addEventListener('click', function () {
          browser.search('', true);
          browser.setLens(b.dataset.dxTab, true);
          var y = (lensTabsRow || inlineEl).getBoundingClientRect().top + window.scrollY - measureTop() - 8;
          window.scrollTo({ top: Math.max(0, y), behavior: reduceMotion ? 'auto' : 'smooth' });
        });
      });
      browser.setLens(params.get('lens'));
      if (params.get('dq')) browser.search(params.get('dq').slice(0, 60), true);
    } else if (panel) {
      browser = new Browser(panel.querySelector('[data-dx-browser]'), {});
      var open = '';
      // The panel ships fixed price bands, so they work without this file. The home's live ones (only
      // bands with cards, the best three fanned in each, in the visitor's currency) come from
      // sections/dex-prices, fetched once the page has settled so the first tap on Price already shows
      // them (fetched on that tap, they swapped in half a second late); their pictures still wait for a
      // tab to be reached for.
      var priceLens = panel.querySelector('[data-dx-lens="price"]');
      var livePrices = function () {
        if (!priceLens || livePrices.p) return;
        livePrices.p = fetch(browser.root.dataset.home + '?section_id=dex-prices', { credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.text() : ''; })
          .then(function (h) {
            var doc = new DOMParser().parseFromString(h, 'text/html');
            var bands = doc.querySelector('.dx-prices');
            if (!bands || !bands.children.length) return;
            var quick = doc.querySelector('.dx-quick');
            priceLens.textContent = '';
            priceLens.appendChild(document.importNode(bands, true));
            if (quick) priceLens.appendChild(document.importNode(quick, true));
            if (warmed) priceLens.querySelectorAll('img').forEach(wake);
            else holdUntilSeen(priceLens);
          })
          .catch(function () { /* the fixed bands stay */ });
      };
      // The panel ends where the screen does, less a strip of dimmed page to tap out on. Measured
      // from its own top, which moves: under the header at the top of the page, under the bar later.
      var fit = function () {
        if (!open) return;
        var top = panel.getBoundingClientRect().top;
        panel.style.maxHeight = Math.max(220, Math.round(window.innerHeight - top - 72)) + 'px';
      };
      var fitRaf = 0;
      var refit = function () { if (open && !fitRaf) fitRaf = requestAnimationFrame(function () { fitRaf = 0; fit(); }); };
      window.addEventListener('scroll', refit, { passive: true });
      window.addEventListener('resize', refit);
      var close = function () {
        if (!open) return;
        open = '';
        panel.hidden = true;
        if (scrim) scrim.hidden = true;
        tabs.forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
      };
      var shown = '';
      var show = function (k) {
        // another tab: its own lens, not the search typed under the last one (a query keeps every lens hidden)
        if (k !== shown && browser.input.value) browser.search('', true);
        shown = k;
        open = k;
        livePrices();
        browser.setLens(k);
        panel.hidden = false;
        if (scrim) scrim.hidden = false;
        panel.scrollTop = 0;
        fit();
        tabs.forEach(function (b) { b.setAttribute('aria-expanded', b.dataset.dxTab === k ? 'true' : 'false'); });
        panel.querySelectorAll('[data-dx-rail]').forEach(function (r) { if (r.__upd) r.__upd(); });
        if (canHover) browser.input.focus({ preventScroll: true });
      };
      tabs.forEach(function (b) {
        b.addEventListener('click', function () { if (open === b.dataset.dxTab) close(); else show(b.dataset.dxTab); });
      });
      if (scrim) scrim.addEventListener('click', close);
      document.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape' || !open) return;
        // focus goes back to the tab that opened the panel
        var from = tabs.filter(function (b) { return b.dataset.dxTab === open; })[0] || tabs[0];
        close();
        if (from) from.focus();
      });
      window.addEventListener('pageshow', function (e) { if (e.persisted) close(); });
    }

    // "/" focuses the Dex search (desktop)
    document.addEventListener('keydown', function (e) {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey || !browser) return;
      var el = document.activeElement;
      if (el && (/INPUT|TEXTAREA|SELECT/.test(el.tagName) || el.isContentEditable)) return;
      if (panel && panel.hidden && tabs[0]) tabs[0].click();
      e.preventDefault();
      browser.input.focus();
      browser.input.select();
    });

    // the bar docks under the header; on the home it arrives once the big tabs scroll away.
    // Its height goes to --dx-bar-h: the theme's sticky Filter / sort row docks under it on phones.
    var ticking = false, barH = 0;
    function onScroll() {
      ticking = false;
      var top = measureTop();
      if (bar && bar.offsetHeight !== barH) { barH = bar.offsetHeight; html.style.setProperty('--dx-bar-h', barH + 'px'); }
      if (isHome && lensTabsRow) {
        var gone = lensTabsRow.getBoundingClientRect().bottom < top + 6;
        bar.classList.toggle('is-tucked', !gone);
      }
    }
    var kick = function () { if (!ticking) { ticking = true; requestAnimationFrame(onScroll); } };
    window.addEventListener('scroll', kick, { passive: true });
    window.addEventListener('resize', kick);
    // Impulse parks and slides the stuck header with no scroll event; re-read it the moment its
    // classes change (a microtask, so the bar's new state lands in the same frame as the header's)
    var header = document.querySelector('.site-header');
    if (header && window.MutationObserver) {
      new MutationObserver(onScroll).observe(header, { attributes: true, attributeFilter: ['class', 'style'] });
    }
    onScroll();

    if (canHover) document.querySelectorAll('[data-dx-rail]').forEach(arrows);
    // Pictures for the first view of each lens. The home fetches the first view of the lens on show
    // once the page has settled; a hidden tab's pictures come when a finger, pointer or focus reaches
    // for its tab (the home's big tabs or the bar's), so nobody pays for a tab they never open (on a
    // phone that was 36 set logos and card fans, ~440 KB). Pokémon and set pages do the same with the
    // panel. The rest of a rail (151 Pokémon, every set) comes the moment someone starts swiping it,
    // ahead of their thumb; fetching it all up front doubled the home's download.
    var WARM = 24;
    var warmLens = function (lens) {
      if (lens.__dxLensWarm) return;
      lens.__dxLensWarm = true;
      Array.prototype.slice.call(lens.querySelectorAll('img[loading="lazy"]'), 0, WARM).forEach(wake);
    };
    var warm = function (scope, shownOnly) {
      scope.querySelectorAll('[data-dx-lens]').forEach(function (lens) { if (!shownOnly || !lens.hidden) warmLens(lens); });
    };
    [inlineEl, panel].forEach(function (scope) {
      if (!scope) return;
      // scroll does not bubble; capture sees each rail (and the panel itself) start to move
      scope.addEventListener('scroll', function (e) {
        var t = e.target;
        if (!t.querySelectorAll || t.__dxWarm) return;
        t.__dxWarm = true;
        t.querySelectorAll('img[loading="lazy"]').forEach(function (img) { if (!img.closest('[hidden]')) wake(img); });
      }, { capture: true, passive: true });
    });
    if (inlineEl) {
      if (document.readyState === 'complete') setTimeout(function () { warm(inlineEl, true); }, 1200);
      else window.addEventListener('load', function () { setTimeout(function () { warm(inlineEl, true); }, 1200); });
      var reach = function (e) {
        var b = e.target.closest && e.target.closest('[data-dx-lenstab], [data-dx-tab]');
        var lens = b && inlineEl.querySelector('[data-dx-lens="' + (b.dataset.dxLenstab || b.dataset.dxTab) + '"]');
        if (lens) warmLens(lens);
      };
      ['pointerdown', 'mouseover', 'focusin'].forEach(function (ev) { document.addEventListener(ev, reach, { passive: true }); });
    } else if (panel) {
      var warmed = false;
      var intent = function () { if (!warmed) { warmed = true; warm(panel); livePrices(); } };
      var settle = function () { setTimeout(livePrices, 1200); };
      if (document.readyState === 'complete') settle();
      else window.addEventListener('load', settle);
      tabs.forEach(function (b) {
        b.addEventListener('pointerdown', intent, { passive: true });
        b.addEventListener('mouseenter', intent, { passive: true });
        b.addEventListener('focus', intent);
      });
    }

    // The theme's filters reload only the grid (theme.js renderCollectionPage). The grid carries the
    // "filtered to" line (snippets/dex-active), so the page says what the cards now are: in the compact
    // header, or, when the landing (whose header is the whole browser, a screen above) is filtered in
    // place, at the top of the cards themselves. The intro goes while a filter is on, as on a reload.
    document.addEventListener('collection:reloaded', function () {
      // the new grid's held photos (the page has long loaded, so the next screen comes too)
      holdUntilSeen(document);
      warmGrid();
      showKind();
      var tpl = document.querySelector('#CollectionAjaxContent template[data-dx-active]');
      if (!tpl) return;
      var line = tpl.content.querySelector('.dx-active');
      var head = document.querySelector('.dx-head--compact');
      if (head) {
        var old = head.querySelector('.dx-active');
        if (old) old.remove();
        if (line) head.appendChild(document.importNode(line, true));
      } else if (line) {
        // the reload replaced the grid, so a line put here last time is already gone
        var col = document.querySelector('#CollectionAjaxContent .grid__item--content');
        if (col) {
          var wrap = document.createElement('div');
          wrap.className = 'dx dx-active-in';
          wrap.appendChild(document.importNode(line, true));
          col.insertBefore(wrap, col.firstChild);
        }
      }
      document.querySelectorAll('.dx-about').forEach(function (el) { el.hidden = !!line; });
    });

    // the tapped Pokémon becomes the next page's hero (only Chromium opts in; see snippets/dex-boot)
    document.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('a[data-dx-mon]');
      if (!a || e.metaKey || e.ctrlKey || e.shiftKey || e.button > 0 || !navigator.userAgentData) return;
      document.querySelectorAll('.dx-sprite').forEach(function (el) { el.style.viewTransitionName = ''; });
      var img = a.querySelector('.dx-sprite');
      if (img) img.style.viewTransitionName = 'dx-hero';
    });
    window.addEventListener('pageshow', function () {
      document.querySelectorAll('[data-dx-mon] .dx-sprite').forEach(function (el) { el.style.viewTransitionName = ''; });
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
